import React from 'react'
import { Button, Form, Alert, Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import L from 'leaflet'

const DrawPanelContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  background: rgba(26, 26, 26, 0.8);
  border-radius: 8px;
  padding: 12px;
  width: 300px;
  color: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
`

interface DrawPanelProps {
  showDrawPanel: boolean
  isDrawingMode: boolean
  handleDrawRectangle: () => void
  isLoadingMap: boolean
  selectedResolution: string
  setSelectedResolution: (value: string) => void
  resolutions: Record<string, { width: number; height: number }>
  bboxBounds: L.LatLngBounds | null
  handleFetchWMSMap: () => void
  errorMessage: string | null
  setShowDrawPanel: (value: boolean) => void
}

const DrawPanel: React.FC<DrawPanelProps> = ({
  showDrawPanel,
  isDrawingMode,
  handleDrawRectangle,
  isLoadingMap,
  selectedResolution,
  setSelectedResolution,
  resolutions,
  bboxBounds,
  handleFetchWMSMap,
  errorMessage,
  setShowDrawPanel,
}) => {
  if (!showDrawPanel) return null

  return (
    <DrawPanelContainer>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Draw Tools</h5>
        <Button
          variant="link"
          size="sm"
          className="text-white p-0"
          onClick={() => setShowDrawPanel(false)}
        >
          <i className="bi bi-x-lg"></i>
        </Button>
      </div>

      <Button
        variant={isDrawingMode ? 'outline-danger' : 'outline-primary'}
        size="sm"
        onClick={handleDrawRectangle}
        disabled={isLoadingMap}
        className="w-100 mb-3"
      >
        {isDrawingMode ? 'Cancel Drawing' : 'Draw Bounding Box'}
      </Button>

      <h6 className="mb-3">Image Settings</h6>
      <Form.Group className="mb-3">
        <Form.Label>Resolution</Form.Label>
        <Form.Select
          size="sm"
          value={selectedResolution}
          onChange={(e) => setSelectedResolution(e.target.value)}
          className="bg-dark text-light border-secondary"
        >
          {Object.keys(resolutions).map((resolution) => (
            <option key={resolution} value={resolution}>
              {resolution}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      {bboxBounds && (
        <div className="d-grid">
          <Button variant="success" size="sm" onClick={handleFetchWMSMap} disabled={isLoadingMap}>
            {isLoadingMap ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-1"
                />
                Loading...
              </>
            ) : (
              'Get WMS Image'
            )}
          </Button>
        </div>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mt-2 p-2 small">
          {errorMessage}
        </Alert>
      )}

      {isDrawingMode && (
        <Alert variant="info" className="mt-2 p-2 small">
          Draw a rectangle on the map to define the bounding box.
        </Alert>
      )}

      {bboxBounds && !isDrawingMode && (
        <Alert variant="info" className="mt-2 p-2 small">
          Bounding box selected. Click &quot;Get WMS Image&quot; to fetch the image.
        </Alert>
      )}
    </DrawPanelContainer>
  )
}

export default DrawPanel
